import { observable, makeObservable } from 'mobx';
import { hubConnection, signalR } from 'signalr-no-jquery';
import RootStore from './root-store';
import { GetUserProfile } from '../utilities/general';

// signalr-no-jquery touches `window` at module scope, so gatsby-node.js
// null-loads it during the build-html/develop-html SSR passes — `signalR`
// (and `hubConnection`) come back undefined there. RootStore constructs this
// class unconditionally (including server-side, to render the initial HTML),
// and observer components read connectionState-derived getters below during
// that same SSR render, so a hard `signalR.connectionState.x` reference would
// crash the build rather than just skip real terminal behaviour. These are
// the real client library's own values (confirmed in
// node_modules/signalr-no-jquery/build/signalR.js) — used as a fallback only
// so SSR sees a consistent "disconnected" state; the real object is always
// used once actually running in the browser.
const CONNECTION_STATE = signalR?.connectionState ?? { connecting: 0, connected: 1, reconnecting: 2, disconnected: 4 };

export type HicapsLogLevel = 'info' | 'success' | 'warning' | 'error';

export interface HicapsLogEntry {
	time: string;
	message: string;
	level: HicapsLogLevel;
}

const DEFAULT_HUB_NAME = 'terminal';

const createRequestId = () => {
	if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}
	return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const describeError = (error: any) => {
	if (!error) return 'Unknown error';
	return error.message || error.source || String(error);
};

export default class HicapsTerminalStore {

	rootStore: RootStore;

	connection: any = null;
	hub: any = null;
	connectionState: number = CONNECTION_STATE.disconnected;
	autoReconnect: boolean = true;
	manuallyDisconnected: boolean = false;
	reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	lastHubUrl: string = '';
	lastHubName: string = DEFAULT_HUB_NAME;

	// Looked up per clinic (see resolveClinicTerminal) instead of the fixed
	// dev-box constants this used to connect to — connectedDeviceIpAddr
	// becomes the hub connect URL, serverIpAddr is what every terminal call's
	// own serverUrl parameter needs. Both empty until a lookup succeeds.
	paymentDeviceServerUrl: string = '';
	// True only while resolveClinicTerminalHubUrl's own API call is in
	// flight — folded into isBusy below so the "connecting" UI (and the
	// Retry button being hidden) covers this phase too, not just the SignalR
	// handshake that comes after it.
	resolvingTerminal: boolean = false;

	logs: HicapsLogEntry[] = [];

	searchTerminalLoading: boolean = false;
	searchRequestId: string = '';
	searchResult: any = null;

	terminalTestLoading: boolean = false;
	terminalTestRequestId: string = '';
	terminalTestResult: any = null;

	saleRequestLoading: boolean = false;
	saleRequestId: string = '';
	saleResult: any = null;

	printLastReceiptLoading: boolean = false;
	printLastReceiptRequestId: string = '';
	printLastReceiptResult: any = null;

	searchMerchantsLoading: boolean = false;
	searchMerchantsRequestId: string = '';
	searchMerchantsResult: any = null;

	searchProvidersLoading: boolean = false;
	searchProvidersRequestId: string = '';
	searchProvidersResult: any = null;

	readCardLoading: boolean = false;
	readCardRequestId: string = '';
	readCardResult: any = null;

	saleCashoutLoading: boolean = false;
	saleCashoutRequestId: string = '';
	saleCashoutResult: any = null;

	claimRequestLoading: boolean = false;
	claimRequestRequestId: string = '';
	claimRequestResult: any = null;

	claimCancelLoading: boolean = false;
	claimCancelRequestId: string = '';
	claimCancelResult: any = null;

	transactionRefundLoading: boolean = false;
	transactionRefundRequestId: string = '';
	transactionRefundResult: any = null;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			connectionState: observable,
			autoReconnect: observable,
			paymentDeviceServerUrl: observable,
			resolvingTerminal: observable,
			logs: observable,
			searchTerminalLoading: observable,
			searchRequestId: observable,
			searchResult: observable,
			terminalTestLoading: observable,
			terminalTestRequestId: observable,
			terminalTestResult: observable,
			saleRequestLoading: observable,
			saleRequestId: observable,
			saleResult: observable,
			printLastReceiptLoading: observable,
			printLastReceiptRequestId: observable,
			printLastReceiptResult: observable,
			searchMerchantsLoading: observable,
			searchMerchantsRequestId: observable,
			searchMerchantsResult: observable,
			searchProvidersLoading: observable,
			searchProvidersRequestId: observable,
			searchProvidersResult: observable,
			readCardLoading: observable,
			readCardRequestId: observable,
			readCardResult: observable,
			saleCashoutLoading: observable,
			saleCashoutRequestId: observable,
			saleCashoutResult: observable,
			claimRequestLoading: observable,
			claimRequestRequestId: observable,
			claimRequestResult: observable,
			claimCancelLoading: observable,
			claimCancelRequestId: observable,
			claimCancelResult: observable,
			transactionRefundLoading: observable,
			transactionRefundRequestId: observable,
			transactionRefundResult: observable,
		});
		this.rootStore = rootStore;
	}

	get isConnected() {
		return this.connectionState === CONNECTION_STATE.connected;
	}

	get isBusy() {
		return this.resolvingTerminal || this.connectionState === CONNECTION_STATE.connecting || this.connectionState === CONNECTION_STATE.reconnecting;
	}

	get connectionStateLabel() {
		switch (this.connectionState) {
			case CONNECTION_STATE.connecting: return 'Connecting';
			case CONNECTION_STATE.connected: return 'Connected';
			case CONNECTION_STATE.reconnecting: return 'Reconnecting';
			case CONNECTION_STATE.disconnected: return 'Disconnected';
			default: return 'Unknown';
		}
	}

	addLog = (message: string, level: HicapsLogLevel = 'info') => {
		this.logs.push({ time: new Date().toLocaleTimeString(), message, level });
	};

	clearLogs = () => {
		this.logs = [];
	};

	setAutoReconnect = (value: boolean) => {
		this.autoReconnect = value;
	};

	// Looks up this clinic's registered Hicaps terminal (POST /api/Clinic/
	// Device/Payment/Search/0/0) instead of connecting to a fixed dev-box
	// address. connectedDeviceIpAddr becomes the hub connect URL (same fixed
	// port/path this always used, just a dynamic IP); serverIpAddr is cached
	// on paymentDeviceServerUrl for every terminal call's own serverUrl
	// parameter to read afterward. Returns null (and logs) if the clinic has
	// no connected device on record.
	resolveClinicTerminalHubUrl = async (): Promise<string | null> => {
		const clinicId = GetUserProfile()?.currentClinicId || 0;
		this.resolvingTerminal = true;
		try {
			const result = await this.rootStore.clinicStore.searchPaymentDevice(clinicId);
			// Search/0/0 returns a list (either a bare array or the usual
			// {items: [...]} wrapper other Search endpoints use), not a single
			// device object — a clinic can have more than one device record on
			// file (e.g. re-registered terminal), so take the most recently
			// created one rather than assuming the first entry is current.
			const list: any[] = Array.isArray(result) ? result : (Array.isArray(result?.items) ? result.items : (result ? [result] : []));
			const device = [...list].sort((a, b) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime())[0];
			this.paymentDeviceServerUrl = device?.serverIpAddr ?? '';
			const deviceIp = device?.connectedDeviceIpAddr;
			if (!deviceIp) {
				this.addLog('No connected Hicaps terminal found for this clinic.', 'error');
				return null;
			}
			return `http://${deviceIp}:51213/healthnex`;
		} catch (err) {
			this.addLog('Failed to look up the Hicaps terminal: ' + describeError(err), 'error');
			return null;
		} finally {
			this.resolvingTerminal = false;
		}
	};

	connect = (hubUrl: string, hubName: string = DEFAULT_HUB_NAME) => {
		if (this.connection && this.connection.state !== CONNECTION_STATE.disconnected) {
			this.addLog('A connection is already active.', 'warning');
			return;
		}

		const normalizedUrl = hubUrl.trim().replace(/\/+$/, '');
		if (!normalizedUrl) {
			this.addLog('Enter the Web Socket URL.', 'error');
			return;
		}

		this.manuallyDisconnected = false;
		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

		this.lastHubUrl = normalizedUrl;
		this.lastHubName = hubName || DEFAULT_HUB_NAME;

		this.connection = hubConnection(normalizedUrl, { useDefaultPath: false });
		this.connection.logging = true;
		this.hub = this.connection.createHubProxy(this.lastHubName);

		this.registerHubHandlers();
		this.registerConnectionEvents();

		this.connectionState = CONNECTION_STATE.connecting;
		this.addLog('Connecting to ' + normalizedUrl, 'info');

		this.connection.start()
			.done(() => {
				this.addLog('Connected. Connection ID: ' + this.connection.id, 'success');
			})
			.fail((error: any) => {
				this.connectionState = CONNECTION_STATE.disconnected;
				this.addLog('Connection failed: ' + describeError(error), 'error');
			});
	};

	disconnect = () => {
		this.manuallyDisconnected = true;
		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

		if (this.connection) this.connection.stop();
		this.connection = null;
		this.hub = null;
		this.connectionState = CONNECTION_STATE.disconnected;

		this.addLog('Connection stopped by the user.', 'info');
	};

	registerConnectionEvents = () => {
		const connection = this.connection;

		connection.stateChanged((change: any) => {
			this.connectionState = change.newState;
		});

		connection.error((error: any) => {
			this.addLog('Web socket error: ' + describeError(error), 'error');
		});

		connection.reconnecting(() => {
			this.addLog('Connection interrupted. Reconnecting...', 'warning');
		});

		connection.reconnected(() => {
			this.addLog('Web socket reconnected successfully.', 'success');
		});

		// Low-level catch-all for every raw message the server pushes, hub
		// method name included — unlike hub.on(...) below, this fires
		// regardless of whether we've registered a handler for that specific
		// event name. Added to debug ClaimCancel/TransactionRefund: the push
		// event names for those two are an unconfirmed guess (see the comment
		// at the top of hicaps-refund-dialog.tsx), and claimCancelResult was
		// observed to never arrive during real testing — this makes the
		// actual event name/payload visible in devtools so that assumption
		// can be corrected once we see what the server really sends.
		connection.received((data: any) => {
			console.log('[Hicaps] raw message received:', data);
		});

		connection.disconnected(() => {
			this.addLog('Web socket disconnected.', 'warning');

			if (!this.manuallyDisconnected && this.autoReconnect) {
				if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
				this.reconnectTimer = setTimeout(() => {
					this.addLog('Attempting a new connection...', 'info');
					this.connect(this.lastHubUrl, this.lastHubName);
				}, 3000);
			}
		});
	};

	registerHubHandlers = () => {
		this.hub.on('searchTerminalsResult', (requestId: string, terminals: any) => {
			this.displaySearchResult(requestId, terminals);
		});

		this.hub.on('testTerminalConnectionResult', (requestId: string, result: any) => {
			this.displayTerminalTestResult(requestId, result);
		});

		this.hub.on('saleResult', (requestId: string, result: any) => {
			this.displaySaleResult(requestId, result);
		});

		this.hub.on('printLastResult', (requestId: string, result: any) => {
			this.displayPrintLastReceiptResult(requestId, result);
		});

		this.hub.on('searchMerchantsResult', (requestId: string, result: any) => {
			this.displaySearchMerchantsResult(requestId, result);
		});

		this.hub.on('searchProvidersResult', (requestId: string, result: any) => {
			this.displaySearchProvidersResult(requestId, result);
		});

		this.hub.on('readCardResult', (requestId: string, result: any) => {
			this.displayReadCardResult(requestId, result);
		});

		this.hub.on('saleCashoutResult', (requestId: string, result: any) => {
			this.displaySaleCashoutResult(requestId, result);
		});

		this.hub.on('claimRequestResult', (requestId: string, result: any) => {
			this.displayClaimRequestResult(requestId, result);
		});

		// Confirmed via real-device testing (2026-09-08): despite every other
		// push event on this hub matching its invoke method name verbatim
		// (ClaimRequest -> claimRequestResult, etc.), ClaimCancel's own push
		// comes back as "cancelClaimResult" — word order reversed, not
		// "claimCancelResult" as originally assumed. Listening on the wrong
		// name meant this result never arrived, so the dialog always read a
		// stale `null` and reported a blank failure even when the terminal
		// genuinely completed.
		this.hub.on('cancelClaimResult', (requestId: string, result: any) => {
			this.displayClaimCancelResult(requestId, result);
		});

		// TransactionRefund's own push event name has NOT been confirmed the
		// same way — it's still the original same-order guess and may turn
		// out to have the identical reversed-order issue as ClaimCancel did.
		// Do not trust this blindly; confirm against a real balance-refund
		// test the same way (check the console for "[Hicaps] raw message
		// received" and look at the "M" field) before relying on it.
		this.hub.on('transactionRefundResult', (requestId: string, result: any) => {
			this.displayTransactionRefundResult(requestId, result);
		});
	};

	displaySearchResult = (requestId: string, terminals: any) => {
		this.searchRequestId = requestId || 'Unknown request';
		this.searchResult = terminals;
		const count = Array.isArray(terminals) ? terminals.length : null;
		this.addLog(count === null ? 'Terminal search result received.' : `Found ${count} terminal(s).`, 'success');
	};

	displayTerminalTestResult = (requestId: string, result: any) => {
		this.terminalTestRequestId = requestId || 'Unknown request';
		this.terminalTestResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Terminal test result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displaySaleResult = (requestId: string, result: any) => {
		this.saleRequestId = requestId || 'Unknown request';
		this.saleResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Sale result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displayPrintLastReceiptResult = (requestId: string, result: any) => {
		this.printLastReceiptRequestId = requestId || 'Unknown request';
		this.printLastReceiptResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Print last receipt result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displaySearchMerchantsResult = (requestId: string, result: any) => {
		this.searchMerchantsRequestId = requestId || 'Unknown request';
		this.searchMerchantsResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Search merchants result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displaySearchProvidersResult = (requestId: string, result: any) => {
		this.searchProvidersRequestId = requestId || 'Unknown request';
		this.searchProvidersResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Search providers result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displayReadCardResult = (requestId: string, result: any) => {
		this.readCardRequestId = requestId || 'Unknown request';
		this.readCardResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Read card result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displaySaleCashoutResult = (requestId: string, result: any) => {
		this.saleCashoutRequestId = requestId || 'Unknown request';
		this.saleCashoutResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Sale & cashout result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displayClaimRequestResult = (requestId: string, result: any) => {
		this.claimRequestRequestId = requestId || 'Unknown request';
		this.claimRequestResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Claim request result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displayClaimCancelResult = (requestId: string, result: any) => {
		this.claimCancelRequestId = requestId || 'Unknown request';
		this.claimCancelResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Claim cancel result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	displayTransactionRefundResult = (requestId: string, result: any) => {
		this.transactionRefundRequestId = requestId || 'Unknown request';
		this.transactionRefundResult = result;
		const responseCode = result && (result.ResponseCode || result.responseCode);
		const responseText = result && (result.ResponseText || result.responseText);
		this.addLog(
			'Transaction refund result' + (responseCode ? ': ' + responseCode : '') + (responseText ? ' - ' + responseText : ''),
			responseCode === '00' ? 'success' : 'warning'
		);
	};

	ping = () => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending Ping.', 'warning');
			return;
		}

		this.addLog('Calling terminal.Ping()...', 'info');

		this.hub.invoke('Ping')
			.done((result: any) => {
				this.addLog('Ping response: ' + String(result), 'success');
			})
			.fail((error: any) => {
				this.addLog('Ping failed: ' + describeError(error), 'error');
			});
	};

	searchTerminals = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before searching for terminals.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.searchRequestId = requestId;
		this.searchResult = null;
		this.searchTerminalLoading = true;

		this.addLog('Sending terminal search request: ' + requestId, 'info');

		this.hub.invoke('SearchTerminals', requestId, serverUrl)
			.done((terminals: any) => {
				if (terminals !== undefined && terminals !== null) {
					this.displaySearchResult(requestId, terminals);
				} else {
					this.addLog('Terminal search request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.searchResult = message;
				this.addLog('Terminal search failed: ' + message, 'error');
			})
			.always(() => {
				this.searchTerminalLoading = false;
			});
	};

	testTerminalConnection = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before running the terminal test.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.terminalTestRequestId = requestId;
		this.terminalTestResult = null;
		this.terminalTestLoading = true;

		this.addLog('Sending terminal test request: ' + requestId, 'info');

		this.hub.invoke('TestTerminalConnection', requestId, serverUrl)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayTerminalTestResult(requestId, result);
				} else {
					this.addLog('Terminal test request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.terminalTestResult = message;
				this.addLog('Terminal test failed: ' + message, 'error');
			})
			.always(() => {
				this.terminalTestLoading = false;
			});
	};

	sendSaleRequest = (serverUrl: string, merchantId: string, transactionAmount: number) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending a sale request.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		if (!merchantId) {
			this.addLog('Enter the merchant ID.', 'warning');
			return;
		}

		if (!Number.isFinite(transactionAmount) || transactionAmount <= 0) {
			this.addLog('Enter a valid transaction amount greater than zero.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.saleRequestId = requestId;
		this.saleResult = null;
		this.saleRequestLoading = true;

		this.addLog(
			`Sending sale request: ${requestId}, merchant: ${merchantId}, amount: $${transactionAmount.toFixed(2)}`,
			'info'
		);

		this.hub.invoke('SendSaleRequest', requestId, serverUrl, merchantId, transactionAmount)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displaySaleResult(requestId, result);
				} else {
					this.addLog('Sale request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.saleResult = message;
				this.addLog('Sale request failed: ' + message, 'error');
			})
			.always(() => {
				this.saleRequestLoading = false;
			});
	};

	printLastReceipt = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before printing the last receipt.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.printLastReceiptRequestId = requestId;
		this.printLastReceiptResult = null;
		this.printLastReceiptLoading = true;

		this.addLog('Sending print last receipt request: ' + requestId, 'info');

		this.hub.invoke('PrintLastReceipt', requestId, serverUrl)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayPrintLastReceiptResult(requestId, result);
				} else {
					this.addLog('Print last receipt request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.printLastReceiptResult = message;
				this.addLog('Print last receipt request failed: ' + message, 'error');
			})
			.always(() => {
				this.printLastReceiptLoading = false;
			});
	};

	searchMerchants = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before searching for merchants.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.searchMerchantsRequestId = requestId;
		this.searchMerchantsResult = null;
		this.searchMerchantsLoading = true;

		this.addLog('Sending search merchants request: ' + requestId, 'info');

		this.hub.invoke('SearchMerchants', requestId, serverUrl)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displaySearchMerchantsResult(requestId, result);
				} else {
					this.addLog('Search merchants request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.searchMerchantsResult = message;
				this.addLog('Search merchants failed: ' + message, 'error');
			})
			.always(() => {
				this.searchMerchantsLoading = false;
			});
	};

	searchProviders = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before searching for providers.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.searchProvidersRequestId = requestId;
		this.searchProvidersResult = null;
		this.searchProvidersLoading = true;

		this.addLog('Sending search providers request: ' + requestId, 'info');

		this.hub.invoke('SearchProviders', requestId, serverUrl)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displaySearchProvidersResult(requestId, result);
				} else {
					this.addLog('Search providers request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.searchProvidersResult = message;
				this.addLog('Search providers failed: ' + message, 'error');
			})
			.always(() => {
				this.searchProvidersLoading = false;
			});
	};

	readCard = (serverUrl: string) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before reading a card.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.readCardRequestId = requestId;
		this.readCardResult = null;
		this.readCardLoading = true;

		this.addLog('Sending read card request: ' + requestId, 'info');

		this.hub.invoke('ReadCard', requestId, serverUrl)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayReadCardResult(requestId, result);
				} else {
					this.addLog('Read card request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.readCardResult = message;
				this.addLog('Read card failed: ' + message, 'error');
			})
			.always(() => {
				this.readCardLoading = false;
			});
	};

	sendSaleCashout = (serverUrl: string, merchantId: string, transactionAmount: number, cashoutAmount: number) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending a sale & cashout request.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		if (!merchantId) {
			this.addLog('Enter the merchant ID.', 'warning');
			return;
		}

		if (!Number.isFinite(transactionAmount) || transactionAmount <= 0) {
			this.addLog('Enter a valid transaction amount greater than zero.', 'warning');
			return;
		}

		if (!Number.isFinite(cashoutAmount) || cashoutAmount <= 0) {
			this.addLog('Enter a valid cashout amount greater than zero.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.saleCashoutRequestId = requestId;
		this.saleCashoutResult = null;
		this.saleCashoutLoading = true;

		this.addLog(
			`Sending sale & cashout request: ${requestId}, merchant: ${merchantId}, sale amount: $${transactionAmount.toFixed(2)}, cash amount: $${cashoutAmount.toFixed(2)}`,
			'info'
		);

		this.hub.invoke('SendSaleCashout', requestId, serverUrl, merchantId, transactionAmount, cashoutAmount)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displaySaleCashoutResult(requestId, result);
				} else {
					this.addLog('Sale & cashout request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.saleCashoutResult = message;
				this.addLog('Sale & cashout request failed: ' + message, 'error');
			})
			.always(() => {
				this.saleCashoutLoading = false;
			});
	};

	claimRequest = (serverUrl: string, merchantId: string, providerNumberId: string, claimAmount: number) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending a claim request.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		if (!merchantId) {
			this.addLog('Enter the merchant ID.', 'warning');
			return;
		}

		if (!Number.isFinite(claimAmount) || claimAmount <= 0) {
			this.addLog('Enter a valid claim amount greater than zero.', 'warning');
			return;
		}

		const requestId = createRequestId();
		this.claimRequestRequestId = requestId;
		this.claimRequestResult = null;
		this.claimRequestLoading = true;

		this.addLog(
			`Sending claim request: ${requestId}, merchant: ${merchantId}, claim amount: $${claimAmount.toFixed(2)}`,
			'info'
		);

		// merchantId is validated but intentionally not sent to the hub method, matching the original app.js call signature.
		this.hub.invoke('ClaimRequest', requestId, serverUrl, providerNumberId, claimAmount)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayClaimRequestResult(requestId, result);
				} else {
					this.addLog('Claim request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.claimRequestResult = message;
				this.addLog('Claim request failed: ' + message, 'error');
			})
			.always(() => {
				this.claimRequestLoading = false;
			});
	};

	// Cancels a previously-paid rebate claim — the provider/RRN pair
	// identifying which claim to cancel is whatever markInsuranceRebateAsPaid
	// stored on the insurance record back when the claim was originally paid.
	claimCancel = (serverUrl: string, providerNumberId: string, rrnNumber: string, fee: number) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending a claim cancel request.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		if (!providerNumberId || !rrnNumber) {
			this.addLog('Missing provider number or RRN for this claim.', 'warning');
			return;
		}

		// Like TransactionRefund, ClaimCancel's own requestId is sent as an
		// empty string rather than a generated one — confirmed fine by the
		// backend for this call, unlike ClaimRequest/SendSaleRequest which
		// still need a real generated id.
		const requestId = '';
		this.claimCancelRequestId = requestId;
		this.claimCancelResult = null;
		this.claimCancelLoading = true;

		this.addLog(
			`Sending claim cancel request: ${requestId}, provider: ${providerNumberId}, rrn: ${rrnNumber}, fee: $${fee.toFixed(2)}`,
			'info'
		);

		this.hub.invoke('ClaimCancel', requestId, serverUrl, providerNumberId, rrnNumber, fee)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayClaimCancelResult(requestId, result);
				} else {
					this.addLog('Claim cancel request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.claimCancelResult = message;
				this.addLog('Claim cancel request failed: ' + message, 'error');
			})
			.always(() => {
				this.claimCancelLoading = false;
			});
	};

	// Refunds a previously-paid balance sale. `merchantId` is whatever
	// makePayment stored on the history record back when the sale was
	// originally paid. CONFIRMED against a real SignalR "method could not be
	// resolved" error (2026-09-08) that the hub only overloads
	// TransactionRefund as (requestId, serverUrl, fee) or (requestId,
	// serverUrl, merchantId, fee) — there is no providerNumberId parameter at
	// all, unlike ClaimCancel. An earlier fix based on a screenshot assumed a
	// 5-param (..., merchantId, providerNumberId, fee) signature; that was
	// wrong and broke every real TransactionRefund call until this fix.
	transactionRefund = (serverUrl: string, merchantId: string, fee: number) => {
		if (!this.hub || !this.isConnected) {
			this.addLog('Connect before sending a transaction refund request.', 'warning');
			return;
		}

		if (!serverUrl) {
			this.addLog('Enter the HICAPS server URL.', 'warning');
			return;
		}

		if (!merchantId) {
			this.addLog('Missing merchant ID for this refund.', 'warning');
			return;
		}

		// Unlike every other call here, TransactionRefund's own requestId is
		// always sent as an empty string rather than a generated one.
		const requestId = '';
		this.transactionRefundRequestId = requestId;
		this.transactionRefundResult = null;
		this.transactionRefundLoading = true;

		this.addLog(
			`Sending transaction refund request, merchant: ${merchantId}, fee: $${fee.toFixed(2)}`,
			'info'
		);

		this.hub.invoke('TransactionRefund', requestId, serverUrl, merchantId, fee)
			.done((result: any) => {
				if (result !== undefined && result !== null) {
					this.displayTransactionRefundResult(requestId, result);
				} else {
					this.addLog('Transaction refund request accepted.', 'success');
				}
			})
			.fail((error: any) => {
				const message = describeError(error);
				this.transactionRefundResult = message;
				this.addLog('Transaction refund request failed: ' + message, 'error');
			})
			.always(() => {
				this.transactionRefundLoading = false;
			});
	};
}
